import { useCallback, useEffect, useMemo, useState } from 'react';
import { Col, Form, Row } from 'antd';
import { Link, useLocation, useParams } from 'react-router-dom';
import { Button } from 'components';
import { useTranslation } from 'react-i18next';
import { EyeOutlined } from '@ant-design/icons';
import ErrorHelper from 'helpers/ErrorHelper';
import ShoppingCentersAPI from 'libs/api/admin/shoppingCenters';
import { useApi } from 'hooks/useApi';
import { useNavigate } from 'hooks/useNavigate';

const api = new ShoppingCentersAPI();

const useShoppingCenter = (isMainPage = true) => {
  const [form] = Form.useForm();
  const { t } = useTranslation();
  const { pathname } = useLocation();
  const navigate = useNavigate();
  const { id } = useParams();
  const [xlsxLoading, setXlsxLoading] = useState(false);

  const {
    data,
    singleItem: singleShop,
    loading,
    onChangeTableFilter,
    onDelete: handleDeleteCategory,
    updateInProcess,
    setUpdateInProcess,
  } = useApi({
    resourceKey: 'shopping_centers',
    singleResourceKey: 'shopping_center',
    api,
    isMainPage,
  });

  const filters = useMemo(() => {
    return [
      {
        title: t('Titolo'),
        type: 'input',
        name: 'by_title',
      },
    ];
  }, [t]);

  const columns = useMemo(() => {
    return [
      {
        title: t('Catena'),
        dataIndex: 'title',
        width: 100,
        sorter: () => {},
      },
      {
        title: '',
        dataIndex: 'actions',
        align: 'right',
        width: 100,
        render: (data, row) =>
          row?.id && (
            <Row justify={'end'}>
              <Col>
                <Link to={`${pathname}/${row.id}`}>
                  <Button type={'secondary'} icon={<EyeOutlined />} />
                </Link>
              </Col>
            </Row>
          ),
      },
    ];
  }, [t, pathname, xlsxLoading]);

  const handleCreateOrUpdateShop = useCallback(
    async (values) => {
      setUpdateInProcess(true);
      try {
        const data = {
          shopping_center: {
            source_name: values.source_name || '',
            source_url: values.source_url || '',
            crawl_depth: values.crawl_depth || '',
            follow_external: values.follow_external || false,
            max_pages: values.max_pages || '',
            content_char_limit: values.content_char_limit || '',
            request_timeout: values.request_timeout || '',
            use_js: values.use_js || false,
            load_more_text: values.load_more_text || '',
            load_more_max_clicks: values.load_more_max_clicks || '',
          },
        };

        if (id) {
          await api.put(id, { data });
          navigate(`/admin/agents/${id}?success=true`);
        } else {
          await api.create({ data });
          navigate('/admin/agents');
        }
      } catch (e) {
        form.setFields(ErrorHelper.getErrorsForForm(e?.response?.data || {}));
      } finally {
        setUpdateInProcess(false);
      }
    },
    [form, navigate, setUpdateInProcess, id, api],
  );

  useEffect(() => {
    if (id && !isMainPage && singleShop) {
      try {
        const fieldsValues = [
          { name: 'source_name', value: singleShop.source_name || '' },
          { name: 'source_url', value: singleShop.source_url || '' },
          { name: 'crawl_depth', value: singleShop.crawl_depth },
          { name: 'follow_external', value: !!singleShop.follow_external },
          { name: 'max_pages', value: singleShop.max_pages },
          { name: 'content_char_limit', value: singleShop.content_char_limit },
          { name: 'request_timeout', value: singleShop.request_timeout },
          { name: 'use_js', value: !!singleShop.use_js },
          { name: 'load_more_text', value: singleShop.load_more_text || '' },
          { name: 'load_more_max_clicks', value: singleShop.load_more_max_clicks },
        ];
        form.setFields(fieldsValues);
      } catch (e) {
        console.log(e);
      }
    }
  }, [id, isMainPage, singleShop, form]);

  return {
    columns,
    data,
    singleShop,
    loading,
    updateInProcess,
    onChangeTableFilter,
    form,
    filters,
    handleCreateOrUpdateShop,
    handleDeleteCategory,
  };
};

export default useShoppingCenter;
